import { AssignedUser } from '@deltek/specification-client/core/Models/listing'
import GridContainer from '@deltek/specification-client/core/Surfaces/GridContainer'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import {
    DefaultTextColor,
    pxToRem,
} from '@deltek/specification-client/core/Utilities/unitUtils'
import Button from '@material-ui/core/Button'
import Divider from '@material-ui/core/Divider'
import Popover from '@material-ui/core/Popover'
import Typography from '@material-ui/core/Typography'
import { Theme, makeStyles } from '@material-ui/core/styles'
import React, { ChangeEvent } from 'react'
import AssignedUserList from '../Assignments/AssignedUserList'
import AutoCompleteAssignMent from '../Assignments/AutocompleteAssignment'
import { PopupButtonProps, StyleProps } from '../Assignments/PopupButton'
import { filterInactiveUsers } from '../ProductDetails'

const useStyles = makeStyles((theme: Theme) => ({
    root: {
        width: '100%',
        maxWidth: 360,
        backgroundColor: theme.palette.background.paper,
        position: 'relative',
        overflow: 'auto',
        maxHeight: 300,
    },
    listSection: {
        backgroundColor: 'inherit',
    },
    ul: {
        backgroundColor: 'inherit',
        padding: 0,
    },
    filterButton: {
        backgroundColor: '#D9F1F0',
        border: '2px solid #02A09C',
        borderRadius: 100,
        height: 38,
    },
    filterButtonEmptySelection: {
        backgroundColor: theme.palette.secondary.light,
        border: '1px solid #02A09C',
        borderRadius: 100,
        height: 38,
    },
    filterButtonOpen: {
        backgroundColor: '#24CEAD',
        border: '1px solid #02A09C',
        borderRadius: 100,
        height: 38,
    },
    popOverBox: {},
    paper: {
        overflowX: 'unset',
        overflowY: 'unset',
        borderRadius: 10,
        '&::before': {
            content: '""',
            position: 'absolute',
            marginRight: '-0.71em',
            top: 0,
            left: 90,
            width: 15,
            height: 15,
            backgroundColor: '#5E5F61',
            boxShadow: theme.shadows[1],
            transform: 'translate(50%, -50%) rotate(-45deg)',
            clipPath:
                'polygon(-5px -5px, calc(100% + 5px) -5px, calc(100% + 5px) calc(100% + 5px))',
        },
    },
    filterButtonText: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '16px',
        color: '#202020',
        textTransform: 'none',
    },
    filterButtonTextEmptySelection: {
        fontStyle: 'normal',
        fontWeight: 'normal',
        fontSize: '14px',
        lineHeight: '16px',
        color: '#5E5F61',
        textTransform: 'none',
    },
    filterButtonTextOpen: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '16px',
        color: theme.palette.secondary.light,
        textTransform: 'none',
    },
    filterBox: {
        width: 360,
        height: 486,
        border: '1px solid #C9C9C9',
        boxShadow: '4px 4px 20px rgba(0, 0, 0, 0.3)',
        borderRadius: 5,
    },
    autoHeight: {
        height: 'auto',
    },
    filterBoxHeaderTextContainer: {
        height: 56,
        padding: 14,
    },
    filterBoxAutocompleteContainer: {
        padding: 14,
        flex: 1,
    },
    filterBoxBottomButtonsContainer: {
        padding: 14,
        height: 71,
    },
    filterBoxHeaderText: {
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: pxToRem(20),
        lineHeight: '25px',
        color: '#047A7D',
        textTransform: 'uppercase',
    },
    divider: {
        backgroundColor: DefaultTextColor,
        height: 2,
    },
    clearButton: {
        border: '1px solid #5E5F61',
        textTransform: 'none',
        borderRadius: 100,
        height: 38,
        paddingLeft: 20,
        paddingRight: 20,
        marginRight: 9,
    },
    closeButton: {
        textTransform: 'none',
        background: '#04797C',
        '&:hover': {
            background: '#036063',
        },
        fontWeight: 'normal',
        borderRadius: 100,
        height: 38,
        paddingLeft: 20,
        paddingRight: 20,
    },
    button: {
        padding: `${theme.spacing(0.75, 1.5, 0.75, 2)}`,
        width: 'auto',
        minWidth: (props: StyleProps) =>
            props.emptyButtonState ? 'auto' : 270,
        height: 41,
        backgroundColor: '#5E5F61',
        color: '#e2e2e2',
        borderRadius: 100,
        ...theme.typography.h6,
        '&:hover': {
            backgroundColor: '#3E3E3E',
        },
        marginBottom: theme.spacing(2),
    },
    contactInfo: {
        //Figma styles. figma.com/file/aoPUWV0jPVw60hyKW0zj37/BPM-1.0-Updates?node-id=702%3A130
        fontSize: pxToRem(15),
        lineHeight: `${theme.spacing(2.25)}px`,
    },
    buttonText: {
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
        color: theme.palette.secondary.light,
    },
    stateButton: {
        lineHeight: '1.95rem',
    },
    notEditableButton: {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    buttonTextOpen: {
        fontSize: pxToRem(18),
        lineHeight: pxToRem(16),
        color: theme.palette.secondary.light,
    },
}))

export const EditFirmContacts: React.FC<Partial<PopupButtonProps>> = ({
    selectPlaceholder,
    name,
    items = [],
    selectedItems = [],
    selectedDisplayItems = [],
    onOpenDropDown,
    onChange,
    onInputChangeDebounced,
    onClose,
    loading = false,
    customFormat = false,
}) => {
    const isSelectionEmpty = true
    const classes = useStyles({ emptyButtonState: isSelectionEmpty })
    const [
        popoverAnchorEl,
        setPopoverAnchorEl,
    ] = React.useState<HTMLButtonElement | null>(null)
    const [openList, handleOpenList] = React.useState<boolean>(false)
    const [isPopoverOpen, handlePopoverOpen] = React.useState<boolean>(false)

    const labelText =
        selectedItems && selectedItems.length > 0
            ? selectedItems.length === 1
                ? `${selectedItems[0]?.profileInfo?.firstName} ${selectedItems[0]?.profileInfo?.lastName}`
                : `${selectedItems[0]?.profileInfo?.firstName} ${
                      selectedItems[0]?.profileInfo?.lastName
                  } (${selectedItems.length - 1} more...)`
            : 'None'

    const handleFilterButtonClick = (
        event: React.MouseEvent<HTMLButtonElement>
    ) => {
        handleOpenList(false)
        setPopoverAnchorEl(event.currentTarget)
        handlePopoverOpen(true)
    }

    const handlePopoverClose = () => {
        setPopoverAnchorEl(null)

        handleOpenList(false)
        handlePopoverOpen(false)

        onClose?.()
    }
    let autocompleteInputEl: HTMLElement
    const onInputRef = (inputElement: HTMLElement) => {
        autocompleteInputEl = inputElement
    }
    const handleChange = (
        event: ChangeEvent<{}>,
        value: Partial<AssignedUser>[]
    ) => {
        onChange?.(event, value)
    }
    const handleClearButtonClick = (
        event: React.MouseEvent<HTMLButtonElement | SVGElement>
    ) => {
        onChange?.(event, [])
        autocompleteInputEl && autocompleteInputEl.focus()
        event.stopPropagation()
    }
    const closeDropDown = () => {
        handleOpenList(false)
        handlePopoverOpen(false)
    }

    return (
        <GridContainer data-testid="assignedUserContactsContainer">
            <GridItem xs>
                <Button
                    disableElevation
                    disableRipple
                    className={classes.button}
                    aria-describedby="Product Contact"
                    variant="contained"
                    onClick={handleFilterButtonClick}
                    data-testid="contacts-filter-button"
                >
                    <Typography
                        className={classes.contactInfo}
                        data-testid="labelText"
                        variant="body2"
                    >
                        {labelText}
                    </Typography>
                </Button>
                <Popover
                    id="bpm-product-contact"
                    anchorEl={popoverAnchorEl}
                    open={isPopoverOpen}
                    onClose={handlePopoverClose}
                    anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                    }}
                    transformOrigin={{
                        vertical: 40,
                        horizontal: -32,
                    }}
                    onEntered={() => {
                        autocompleteInputEl && autocompleteInputEl.focus()
                    }}
                    classes={{
                        paper: !openList ? '' : classes.paper,
                    }}
                    marginThreshold={7}
                >
                    {!openList ? (
                        <GridContainer
                            direction="column"
                            className={classes.filterBox}
                        >
                            <GridItem
                                className={classes.filterBoxHeaderTextContainer}
                            >
                                <Typography
                                    className={classes.filterBoxHeaderText}
                                >
                                    Product Contact
                                </Typography>
                            </GridItem>
                            <GridItem>
                                <Divider className={classes.divider} />
                            </GridItem>
                            <GridItem
                                className={
                                    classes.filterBoxAutocompleteContainer
                                }
                            >
                                {
                                    <AutoCompleteAssignMent
                                        name={`${name}-autocomplete`}
                                        items={filterInactiveUsers(items)}
                                        placeholder={
                                            isSelectionEmpty
                                                ? selectPlaceholder
                                                : ''
                                        }
                                        onChange={handleChange}
                                        onInputChangeDebounced={
                                            onInputChangeDebounced
                                        }
                                        onOpen={onOpenDropDown}
                                        onClose={closeDropDown}
                                        selectedItems={selectedItems}
                                        selectedDisplayItems={
                                            selectedDisplayItems
                                        }
                                        onInputRef={onInputRef}
                                        loading={loading}
                                        customFormat={customFormat}
                                    />
                                }
                            </GridItem>
                            <GridItem>
                                <Divider className={classes.divider} />
                            </GridItem>
                            <GridItem
                                container
                                className={
                                    classes.filterBoxBottomButtonsContainer
                                }
                                direction="row"
                                justifyContent="flex-end"
                            >
                                {!openList && (
                                    <Button
                                        disableElevation
                                        data-testid="clear-button"
                                        className={classes.clearButton}
                                        onClick={handleClearButtonClick}
                                    >
                                        Clear
                                    </Button>
                                )}
                                <Button
                                    disableElevation
                                    variant="contained"
                                    color="primary"
                                    data-testid="close-button"
                                    className={classes.closeButton}
                                    onClick={handlePopoverClose}
                                >
                                    Close
                                </Button>
                            </GridItem>
                        </GridContainer>
                    ) : (
                        <AssignedUserList
                            items={selectedItems ?? []}
                            buttonVariant={'BPM'}
                        />
                    )}
                </Popover>
            </GridItem>
        </GridContainer>
    )
}
